// Components/Header.tsx
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const name = localStorage.getItem("userName");
    const email = localStorage.getItem("userEmail");
    setIsAuthenticated(!!token);
    if (name) setUserName(name);
    if (email) setUserEmail(email);
  }, [location]);

  useEffect(() => {
    if (!showDropdown) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (dropdownRef.current?.contains(target)) return;
      setShowDropdown(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setShowDropdown(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown, {
      passive: true,
    });
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showDropdown]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("user");
    localStorage.removeItem("userId");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("userName");
    localStorage.removeItem("userRole");
    setIsAuthenticated(false);
    setShowDropdown(false);
    navigate("/login");
  };

  const getAvatarLetter = () =>
    userName && userName.length > 0 ? userName.charAt(0).toUpperCase() : "U";

  const getAvatarGradient = () => {
    const gradients = [
      "from-emerald-400 to-emerald-600",
      "from-teal-400 to-teal-600",
      "from-green-400 to-green-600",
      "from-cyan-400 to-cyan-600",
      "from-sky-400 to-sky-600",
      "from-lime-400 to-lime-600",
      "from-emerald-500 to-teal-600",
      "from-teal-400 to-emerald-500",
    ];
    if (userName) return gradients[userName.length % gradients.length];
    return "from-emerald-400 to-emerald-600";
  };

  const navItems = [
    { path: "/", label: "Home", icon: "🏆" },
    { path: "/history", label: "History", icon: "📜" },
  ];

  const isActive = (path: string) => location.pathname === path;

  if (location.pathname === "/login" || location.pathname === "/register") {
    return null;
  }

  return (
    <header className="bg-white/80 backdrop-blur-md border-b border-emerald-100 sticky top-0 z-50">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* ── Brand ── */}
          <Link
            to={isAuthenticated ? "/" : "/login"}
            className="flex items-center no-underline"
          >
            {/* Logo image from public folder */}
            <img
              src="/card-grader-logo.png"
              alt="CardGrader Logo"
              className="w-[100px] h-[100px] rounded-xl object-contain ml-[-30px]"
            />
            <div className="flex flex-col leading-none ml-[-15px]">
              <span className="text-lg font-black tracking-tight bg-gradient-to-r from-emerald-800 via-emerald-600 to-emerald-400 bg-clip-text text-transparent">
                CardGrader
              </span>
            </div>
          </Link>

          {/* ── Desktop Right ── */}
          <div className="hidden md:flex items-center gap-3">
            {isAuthenticated ? (
              <div ref={dropdownRef} className="relative">
                {/* Avatar Button */}
                <button
                  onClick={() => setShowDropdown((prev) => !prev)}
                  aria-haspopup="menu"
                  aria-expanded={showDropdown}
                  className="flex items-center gap-2.5 px-3 py-1.5 rounded-xl hover:bg-emerald-50 transition-all duration-200 focus:outline-none group"
                >
                  <div
                    className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarGradient()} flex items-center justify-center text-white font-bold text-sm transition-transform group-hover:scale-105`}
                  >
                    {getAvatarLetter()}
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-bold text-gray-800 leading-none mt-3">
                      {userName}
                    </p>
                  </div>
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showDropdown ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {/* Dropdown */}
                {showDropdown && (
                  <div className="absolute right-0 mt-2 w-52 bg-white rounded-2xl border border-emerald-100 py-2 z-10">
                    {/* User info */}
                    <div className="px-4 py-3 border-b border-gray-100 mb-1">
                      <div className="flex items-center gap-2.5">
                        <div
                          className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarGradient()} flex items-center justify-center text-white font-bold text-sm`}
                        >
                          {getAvatarLetter()}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-800">
                            {userName}
                          </p>
                          <p className="text-xs text-gray-400">{userEmail}</p>
                        </div>
                      </div>
                    </div>

                    <Link
                      to="/profile"
                      onClick={() => setShowDropdown(false)}
                      className="flex items-center gap-2.5 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors no-underline"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      Profile
                    </Link>

                    <Link
                      to="/history"
                      onClick={() => setShowDropdown(false)}
                      className="flex items-center gap-2.5 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors no-underline"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      History
                    </Link>

                    <div className="border-t border-gray-100 my-1" />

                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2.5 px-4 py-2 text-sm font-semibold text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm font-bold text-gray-500 hover:text-emerald-600 transition-colors duration-200 no-underline"
                >
                  Log in
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-2 text-sm font-extrabold text-white bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600 rounded-xl hover:-translate-y-0.5 transition-all duration-200 no-underline"
                >
                  Get started
                </Link>
              </>
            )}
          </div>

          {/* ── Mobile Hamburger ── */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-xl text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 focus:outline-none transition-colors"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isMobileMenuOpen ? (
                <path d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* ── Mobile Menu ── */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-emerald-100">
            <div className="flex flex-col gap-2">
              {isAuthenticated ? (
                <>
                  {/* Mobile user info */}
                  <div className="flex items-center gap-3 px-3 py-2.5 bg-emerald-50 rounded-xl mb-1">
                    <div
                      className={`w-10 h-10 rounded-full bg-gradient-to-br ${getAvatarGradient()} flex items-center justify-center text-white font-bold text-sm`}
                    >
                      {getAvatarLetter()}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-800">
                        {userName}
                      </p>
                      <p className="text-xs text-gray-400">{userEmail}</p>
                    </div>
                  </div>

                  {navItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-bold transition-colors no-underline ${
                        isActive(item.path)
                          ? "text-emerald-700 bg-emerald-50"
                          : "text-gray-600 hover:text-emerald-700 hover:bg-emerald-50"
                      }`}
                    >
                      <span>{item.icon}</span>
                      <span>{item.label}</span>
                    </Link>
                  ))}

                  <div className="pt-2 border-t border-emerald-100">
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-bold text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                      Logout
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {navItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-bold transition-colors no-underline ${
                        isActive(item.path)
                          ? "text-emerald-700 bg-emerald-50"
                          : "text-gray-600 hover:text-emerald-700 hover:bg-emerald-50"
                      }`}
                    >
                      <span>{item.icon}</span>
                      <span>{item.label}</span>
                    </Link>
                  ))}
                  <div className="pt-2 flex flex-col gap-2 border-t border-emerald-100">
                    <Link
                      to="/login"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="px-3 py-2.5 text-sm font-bold text-gray-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-xl transition-colors no-underline"
                    >
                      Log in
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="px-3 py-2.5 text-sm font-extrabold text-white text-center bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600 rounded-xl transition-all no-underline"
                    >
                      Get started
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
